import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Crown, Star, Diamond, Gem, Shield, Award } from 'lucide-react';

interface VipTierDisplayProps {
  tierName: string;
  tierLevel: number;
  tierColor: string;
  tierReachRewardGc?: number;
  tierReachRewardSc?: number;
  weeklyRewardPercentage?: number;
  maxWeeklyRewardGc?: number;
  maxWeeklyRewardSc?: number;
  monthlyGcRequired?: number;
  dailyWithdrawalLimit?: number | null;
  dailyWithdrawalCountLimit?: number;
  bonusMultiplier?: number;
  vipAccess?: boolean;
  isCurrentTier?: boolean;
  showDetails?: boolean;
  className?: string;
  // Progress tracking
  currentProgress?: number;
  nextTierRequirement?: number;
  totalWagered?: number;
  // Backend-calculated progress values (preferred for consistency)
  backendProgressPercent?: number;
  backendPointsToNextTier?: number;
}

export const VipTierDisplay: React.FC<VipTierDisplayProps> = ({
  tierName,
  tierLevel,
  tierColor,
  tierReachRewardGc = 0,
  tierReachRewardSc = 0,
  weeklyRewardPercentage = 0,
  maxWeeklyRewardGc = 0,
  maxWeeklyRewardSc = 0,
  monthlyGcRequired = 0,
  dailyWithdrawalLimit = null,
  vipAccess = false,
  isCurrentTier = false,
  showDetails = true,
  className = "",
  currentProgress = 0,
  nextTierRequirement = 0,
  totalWagered = 0,
  backendProgressPercent,
  backendPointsToNextTier
}) => {
  const getTierIcon = (level: number) => {
    switch (level) {
      case 1: return Shield;
      case 2: return Award;
      case 3: return Star;
      case 4: return Crown;
      case 5: return Diamond;
      case 6: return Gem;
      case 7: return Diamond;
      case 8: return Crown;
      default: return Shield;
    }
  };

  const TierIcon = getTierIcon(tierLevel);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}K`;
    }
    return num.toString();
  };

  // Use backend-calculated progress if available, otherwise calculate locally
  const progressPercentage = backendProgressPercent !== undefined 
    ? backendProgressPercent 
    : (isCurrentTier && nextTierRequirement > 0 
        ? Math.min(Math.max((totalWagered / nextTierRequirement) * 100, 0), 100) 
        : 0);
  
  const remainingToNextTier = backendPointsToNextTier !== undefined 
    ? backendPointsToNextTier 
    : Math.max(0, nextTierRequirement - totalWagered);

  // Compact sidebar version - Casino Style (single row layout)
  if (!showDetails) {
    return (
      <div 
        className={`relative overflow-hidden rounded-xl transition-all duration-300 ${className}`}
        style={{
          background: `linear-gradient(145deg, ${tierColor}25 0%, hsl(var(--card) / 0.95) 50%, ${tierColor}15 100%)`,
          boxShadow: `0 0 20px ${tierColor}20, inset 0 1px 0 hsl(var(--border) / 0.3)`,
          border: `1.5px solid ${tierColor}40`,
        }}
      >
        {/* Static glow */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{ 
            background: `radial-gradient(ellipse at 50% 0%, ${tierColor}30 0%, transparent 70%)`,
          }}
        />

        {/* Diagonal stripes */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 8px, ${tierColor} 8px, ${tierColor} 9px)`,
          }}
        />

        <div className="relative px-3 py-2.5 z-10 h-full flex flex-col justify-center gap-1">
          {/* Top row: Tier Name + VIP badge */}
          <div className="flex items-center gap-1.5">
            <h3 
              className="font-black text-xs uppercase text-foreground"
              style={{ textShadow: `0 0 10px ${tierColor}60` }}
            >
              {tierName}
            </h3>
          </div>

          {/* Progress bar + percentage */}
          {isCurrentTier && nextTierRequirement > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-foreground/10 rounded-full h-1.5 overflow-hidden">
                <div 
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{ 
                    width: tierLevel === 8 ? '100%' : `${progressPercentage}%`, 
                    background: `linear-gradient(90deg, ${tierColor}, ${tierColor}cc)`,
                    boxShadow: `0 0 6px ${tierColor}80`
                  }}
                />
              </div>
              <span className="text-[10px] font-bold text-foreground/70 flex-shrink-0">
                {tierLevel === 8 ? "MAX" : `${Math.round(progressPercentage)}%`}
              </span>
            </div>
          )}

          {/* Points to next tier */}
          {isCurrentTier && tierLevel < 8 && remainingToNextTier > 0 && (
            <div className="text-[9px] text-muted-foreground">
              {formatNumber(remainingToNextTier)} pts to next tier
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full details version for main page
  return (
    <div 
      className={`
        relative p-4 border rounded-lg transition-all duration-300
        ${isCurrentTier 
          ? 'border-primary bg-primary/5 shadow-lg ring-2 ring-primary/20' 
          : 'border-border bg-card hover:bg-muted/30'
        }
        ${className}
      `}
      style={{
        borderColor: isCurrentTier ? tierColor : undefined,
        boxShadow: isCurrentTier ? `0 0 20px ${tierColor}20` : undefined
      }}
    >
      {/* Current Tier Badge */}
      {isCurrentTier && (
        <div className="absolute -top-2 -right-2">
          <Badge 
            variant="default" 
            className="bg-primary text-primary-foreground shadow-md"
          >
            CURRENT LEVEL
          </Badge>
        </div>
      )}

      {/* Tier Header */}
      <div className="flex items-center gap-3 mb-3">
        <div 
          className="p-2 rounded-lg"
          style={{ backgroundColor: `${tierColor}20` }}
        >
          <TierIcon 
            className="h-6 w-6" 
            style={{ color: tierColor }}
          />
        </div>
        <div>
          <h3 
            className="font-bold text-lg" 
            style={{ color: tierColor }}
          >
            {tierName}
          </h3>
        </div>
      </div>

      {/* Tier Details */}
      <div className="space-y-3">
        {/* Tier Reach Rewards */}
        {(tierReachRewardGc > 0 || tierReachRewardSc > 0) && (
          <div className="bg-muted/50 p-3 rounded-lg">
            <span className="text-sm font-medium text-muted-foreground mb-2 block">Tier Reach Bonus:</span>
            <div className="flex gap-4">
              {tierReachRewardGc > 0 && (
                <span className="text-sm font-medium text-yellow-600">
                  {formatNumber(tierReachRewardGc)} GC
                </span>
              )}
              {tierReachRewardSc > 0 && (
                <span className="text-sm font-medium text-blue-600">
                  {formatNumber(tierReachRewardSc)} SC
                </span>
              )}
            </div>
          </div>
        )}

        {/* Weekly Reward Percentage */}
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">Weekly Reward:</span>
          <span className="font-medium text-green-600">{weeklyRewardPercentage}%</span>
        </div>

        {/* Max Weekly Rewards */}
        {(maxWeeklyRewardGc > 0 || maxWeeklyRewardSc > 0) && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Max Weekly:</span>
            <div className="flex gap-2">
              {maxWeeklyRewardGc > 0 && (
                <span className="text-sm font-medium text-yellow-600">
                  {formatNumber(maxWeeklyRewardGc)} GC
                </span>
              )}
              {maxWeeklyRewardSc > 0 && (
                <span className="text-sm font-medium text-blue-600">
                  {formatNumber(maxWeeklyRewardSc)} SC
                </span>
              )}
            </div>
          </div>
        )}

        {/* VIP Access */}
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">VIP Access:</span>
          <Badge variant={vipAccess ? "default" : "secondary"}>
            {vipAccess ? "Yes" : "No"}
          </Badge>
        </div>

        {/* Daily Withdrawal Limit */}
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">Daily Withdrawal:</span>
          <span className="font-medium text-blue-600">
            {dailyWithdrawalLimit ? `${Math.round(dailyWithdrawalLimit).toLocaleString()} SC` : 'Unlimited'}
          </span>
        </div>

        {/* GC Required */}
        {monthlyGcRequired > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">GC Required:</span>
            <span className="font-medium text-yellow-600">
              {formatNumber(monthlyGcRequired)}/month
            </span>
          </div>
        )}
      </div>
    </div>
  );
};